import React from "react";
import { useSelector } from "react-redux";
import { Trophy, Coins } from "lucide-react";
import Countdown from "./Countdown";
import { selectBondWheelParticipation } from "../../redux/bondWheelSlice";
import {
  formatCoin,
  getGameMinimumPlayers,
  getGameNumberOfWinners,
  getGameWinnerEntries,
  getGameWinnerName,
  getUserTicketCount,
} from "../../utils/bondWheelHelpers";

const BondWheelCard = ({ game, userId, onOpen, onJoin, joining }) => {
  const { canJoin, isParticipant, isCreator } = useSelector((state) =>
    selectBondWheelParticipation(state, game?._id, userId)
  );

  const isSettled = game?.status === "Settled";
  const isCancelled = game?.status === "Cancelled";
  const isLocked = game?.status === "Locked";
  const isLive = !isSettled && !isCancelled;
  const playerCount =
    game?.entries?.filter((e) => ["Active", "Won", "Lost"].includes(e.status))?.length || 0;
  const minimumPlayers = getGameMinimumPlayers(game);
  const numberOfWinners = getGameNumberOfWinners(game);
  const minMet = playerCount >= minimumPlayers;
  const winnerEntries = getGameWinnerEntries(game);
  const winnerName = getGameWinnerName(game);
  const userTicketCount = getUserTicketCount(game, userId);

  return (
    <article className={`bw-card ${isCreator ? "bw-card-host" : isParticipant ? "bw-card-in" : ""}`}>
      <button type="button" className="bw-card-main" onClick={() => onOpen?.(game)}>
        <div className="bw-card-top">
          <div>
            <p className="bw-card-host-name">{game?.creator?.name || "Host"}</p>
            <p className="bw-card-status">
              {isSettled ? "Settled" : isCancelled ? "Cancelled" : isLocked ? "Locked" : "Live"}
            </p>
          </div>
          {isLive ? (
            <Countdown deadline={game?.deadline} tone={isLocked ? "lock" : "live"} />
          ) : isSettled ? (
            <span className="bw-card-end bw-card-end-settled">
              <Trophy size={12} /> Settled
            </span>
          ) : (
            <span className="bw-card-end bw-card-end-cancelled">Cancelled</span>
          )}
        </div>

        <div className="bw-card-pot">
          <span className="bw-card-pot-label">Prize pot</span>
          <span className="bw-card-pot-value">
            <Coins size={14} /> {formatCoin(game?.totalPot)} COIN
          </span>
          {numberOfWinners > 1 ? (
            <span className="bw-card-pot-sub">{numberOfWinners} winners · equal split</span>
          ) : null}
        </div>

        <div className="bw-card-stats">
          <div className="bw-card-stat">
            <span className="bw-card-stat-label">Entry</span>
            <strong className="bw-card-stat-value">{formatCoin(game?.entryAmount)}</strong>
          </div>
          <div className="bw-card-stat">
            <span className="bw-card-stat-label">Joined</span>
            <strong className={`bw-card-stat-value ${!minMet && isLive ? "bw-card-stat-warn" : ""}`}>
              {playerCount}
            </strong>
            {!minMet && isLive ? (
              <span className="bw-card-stat-sub">
                need {Math.max(0, minimumPlayers - playerCount)} more
              </span>
            ) : null}
          </div>
          <div className="bw-card-stat">
            <span className="bw-card-stat-label">Min</span>
            <strong className="bw-card-stat-value">{minimumPlayers}</strong>
          </div>
          <div className="bw-card-stat">
            <span className="bw-card-stat-label">Winners</span>
            <strong className="bw-card-stat-value">{numberOfWinners}</strong>
          </div>
        </div>

        {isSettled && winnerEntries.length > 0 ? (
          <p className="bw-card-winner">
            {winnerEntries.length > 1
              ? `${winnerEntries.length} winners: ${winnerName}`
              : `${winnerName} won ${formatCoin(winnerEntries[0]?.netPayout ?? game?.winnerNetPayout)} COIN`}
          </p>
        ) : null}
      </button>

      {isLive && canJoin ? (
        <button
          type="button"
          className="bw-card-join"
          disabled={joining}
          onClick={() => onJoin?.(game)}
        >
          {joining
            ? "Adding…"
            : userTicketCount > 0
              ? `Add ticket · ${formatCoin(game?.entryAmount)} COIN`
              : `Join · ${formatCoin(game?.entryAmount)} COIN`}
        </button>
      ) : null}
    </article>
  );
};

export default BondWheelCard;
